import i18n, { t } from '@if/core/i18n'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { Alert, Card, CardContent, Drawer, Stack, Switch, Typography } from '@mui/material'
import { useFraudFlags } from '@src/data/api/fraud/useFraud'
import { DocumentCategoryFlagsToUse, DocumentFlagsToShow } from '@src/data/types/constants'
import { SetStateAction, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import DocumentFraudFlagsCard from './documentFraudFlagsCard'


interface documentFraudFlagsProps { 
  creditApplicationId: string
  originalGlobalApplicantType: string
  financingProgramId: EFinancingProgram
  fraudFlagsPanelOpen: boolean
  selectedPagesIds?: string[]
  setFraudFlagsPanelOpen: (value: SetStateAction<boolean>) => void
  setNumberActiveFlags: (value: SetStateAction<number>) => void
}
const DocumentFraudFlags: React.FC<documentFraudFlagsProps> = ({
  creditApplicationId,
  originalGlobalApplicantType,
  financingProgramId,
  fraudFlagsPanelOpen,
  selectedPagesIds,
  setFraudFlagsPanelOpen,
  setNumberActiveFlags
}) => {  
  const [indicatorType, setIndicatorType] = useState<EApplicantType>(EApplicantType.Applicant)
  const [fraudFlagsData, isLoading] = useFraudFlags(financingProgramId, creditApplicationId || '')

  const flagsByCategory = useMemo(() => {
    if (!fraudFlagsData?.flags) return {}

    const flags = fraudFlagsData.flags[indicatorType]

    let numberActive = 0
    const grouped: Record<string, typeof flags> = {}
    flags.forEach((flag) => {
      if (DocumentCategoryFlagsToUse.includes(flag.category) && DocumentFlagsToShow(financingProgramId).includes(flag.key)) {
        if (!grouped[flag.category]) {
          grouped[flag.category] = []
        }
        grouped[flag.category].push(flag)
        if (flag.value) {
          numberActive += 1
        }
      }
    })
    setNumberActiveFlags(numberActive)

    return grouped
  }, [fraudFlagsData, setNumberActiveFlags, financingProgramId, indicatorType])

  const asCoapplicant = useMemo(() => {
    if (!fraudFlagsData?.flags) return {}

    const flags = fraudFlagsData.flags[EApplicantType.CoApplicant]

    return flags !== undefined
  }, [fraudFlagsData])

  useEffect(() => {
    setIndicatorType(originalGlobalApplicantType as EApplicantType)
  }, [originalGlobalApplicantType])

  const pageSelected = selectedPagesIds && selectedPagesIds.length > 0;

  return (
    <Drawer
      anchor='right'
      open={fraudFlagsPanelOpen}
      onClose={() => setFraudFlagsPanelOpen(false)}
      slotProps={{ paper: { sx: { width: '30vw', display: 'flex', flexDirection: 'column', height: '100%' } } }}>
      {isLoading && (
        <>
          <Trans>{t('common.loading')}</Trans>
        </>
      )}
      {!isLoading && (
        <>
          <Card sx={{ padding: 2, flexShrink: 0 }}>
            <Stack direction="column">
              <Typography variant="h5" sx={{ wordWrap: 'break-word', justifyContent: 'center', display: 'flex' }}>
                {t('fraudFlags.title')}
              </Typography>
              <div style={{ marginLeft: 'auto', justifyContent: 'flex-end', display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ textAlign: 'right' }}>
                  {t('common.applicant')}s
                  <Switch
                    disabled={!asCoapplicant}
                    checked={indicatorType !== EApplicantType.Applicant}
                    onChange={() => setIndicatorType((prev) => prev === EApplicantType.Applicant ? EApplicantType.CoApplicant : EApplicantType.Applicant)}
                  />
                  {t('common.coApplicant')}
                </Typography>
              </div>              
              {!pageSelected && (
                <Alert severity="warning">
                  <Typography>
                    {t('fraudFlags.warningNoDocSelected')}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Card>
          <Card sx={{ padding: 1, overflow: 'auto', flex: 1 }}>
            <CardContent>
              <Stack direction="column" spacing={1.5} >
                {Object.entries(flagsByCategory).map(([category, flags]) => (
                  <Stack direction="column" spacing={1.5} key={category}>
                    {Object.entries(flags).map(([key, flag]) => (
                      <DocumentFraudFlagsCard
                        key={key}
                        indicatorType={indicatorType}
                        flag={flag}
                        currentLanguage={i18n.language}
                        financingProgramId={financingProgramId}
                        creditApplicationId={creditApplicationId}
                        selectedPagesIds={selectedPagesIds || []}
                      />
                    ))}
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Drawer>
  )
}

export default DocumentFraudFlags